// Define the custom block type locally to avoid circular dependencies
const CustomBlockType = {
	CRITERIA_BLOCK: 'criteria_block' as const,
};
import { createCustomBlock } from '../../../utils/createCustomBlock';
import { t } from '../../../utils/I18nManager';
import { resolvePreviewColor } from '@core/utils';
import { getCriteriaListings } from '../../../utils/listingCache';
import { buildListingDataAttr } from '../../../utils/listingDataAttr';
import {
	isListingUrlTag,
	previewListingUrl,
	resolveListingHref,
	stripEmptyListingHrefs,
	toLoopListingHref,
} from '../../../utils/resolveListingHref';
import { BasicType } from '../../../constants';
import { IBlockData, RecursivePartial } from '../../../typings';
import { BasicBlock } from '../../../components/BasicBlock';
import { merge } from 'lodash';
import * as React from 'react';
import * as Handlebars from 'handlebars';
import { listingLayouts } from '../../../data';
import { processHtmlMergeTags } from '../../../utils/getMergeTagImageUrl';
import { getProxiedImageUrl } from '../../../utils/getProxiedImageUrl';
import { getListingPlaceholderImage } from '../../../utils/getImg';
import { registerDynamicTextHandlebarsHelpers } from '../../../utils/dynamicTextFormatHelpers';

// Register the raw helper for preserving merge tags in production mode
Handlebars.registerHelper('raw', function (options) {
	return options.fn();
});
registerDynamicTextHandlebarsHelpers(Handlebars);

interface ListingItem {
	id: string;
	address?: string;
	streetaddress?: string;
	city?: string;
	stateProvince?: string;
	state?: string;
	postalCode?: string;
	zip_code?: string;
	list_price?: number | string;
	beds?: number;
	baths?: number;
	sq_foot?: number;
	image_data?: string[];
	url?: string;
	listingurl?: string;
}

interface CriteriaConfig {
	// Listing settings
	maxListings?: number;

	// Market criteria
	criteriaType?: 'map' | 'area';
	locationType?: 'city' | 'county' | 'state' | 'zip_code' | 'area' | 'subarea';
	city?: string;
	county?: string;
	state?: string;
	zip_code?: string;
	area?: string;
	subarea?: string;

	// Map specific
	address?: string;
	lat?: number;
	lng?: number;
	zoomLevel?: number;

	// Property selection
	propertyType?: 'residential' | 'multifamily' | 'commercial' | 'land';
	propertyStyles?: string[];

	// Range filters
	priceMin?: number;
	priceMax?: number;
	sqftMin?: number;
	sqftMax?: number;
	bedsMin?: number;
	bedsMax?: number;
	bathsMin?: number;
	bathsMax?: number;
	unitsMin?: number;
	unitsMax?: number;
	buildingsMin?: number;
	buildingsMax?: number;
	acreageMin?: number;
	acreageMax?: number;
	yearBuilt?: number;
	officeSqftMin?: number;
	officeSqftMax?: number;

	// Features/amenities
	golfCourse?: boolean;
	waterFront?: boolean;
	propertyView?: boolean;
}

type ListingSortBy = 'price_desc' | 'price_asc' | 'date_desc' | 'date_asc';

interface ICriteriaBlockData {
	maxCount: number;
	sortBy?: ListingSortBy;
	/**
	 * The binding. A criteria block is bound to a *query*, not to listings —
	 * this is the only listing state that gets persisted.
	 */
	criteriaConfig: CriteriaConfig;
	/** The drawer's raw form config (snake_case) - what the listings search expects. */
	originalConfig?: CriteriaConfig;
	/**
	 * @deprecated Matching listings are no longer saved. A stored result set is
	 * a snapshot: it keeps showing properties that have since sold, and misses
	 * ones that have since come on the market. Read at load time as a first
	 * paint for pre-migration designs, then dropped. Never write it.
	 */
	criteriaResults?: ListingItem[];
	/** @deprecated Derived from the live result set. See `criteriaResults`. */
	criteriaResultCount?: number;
	/**
	 * Stable per-instance identity, generated once (lazily, on first criteria
	 * save - see the attribute panel's ensureCriteriaBlockId) and never
	 * regenerated afterward. A criteria block has no natural id of its own the
	 * way a listing composite id gives Single/Multiple/Custom - two blocks can
	 * easily end up with byte-identical criteriaConfig, so this is what a
	 * send-time pipeline keys off instead of the filter content itself.
	 */
	criteriaBlockId?: string;
}

/**
 * The persisted binding for a criteria block.
 *
 * SEND-TIME SEAM — see docs/LISTING_BINDING_CONTRACT.md. Unlike the other two
 * listing blocks this binds to a *query*, so dispatch has to run the search
 * rather than look ids up. Whatever it matches at send time is what goes out —
 * that is the point of the block, and why its results are no longer stored.
 *
 * TODO(vignesh): the send pipeline needs to run this config at dispatch. It
 * cannot fall back to the deprecated `criteriaResults`, which are no longer
 * written.
 */
export function getCriteriaBlockConfig(
	value?: ICriteriaBlockData | null,
): CriteriaConfig | undefined {
	// The drawer's raw form config is what the search expects; criteriaConfig is a
	// display transform that matches nothing (monday 13121451389). Older blocks
	// saved before originalConfig existed fall back to it.
	return value?.originalConfig || value?.criteriaConfig;
}

/**
 * The persisted criteriaBlockId, falling back to an idx-derived id for a
 * design saved before this field existed - same idea as Image/index.tsx's
 * img_dyn_${idx} fallback for a pre-ensureDynamicImageId design. The fallback
 * is NOT persisted here; it's only stable for as long as this block's idx
 * doesn't change, unlike the real persisted id.
 */
export function getCriteriaBlockId(
	value: ICriteriaBlockData | null | undefined,
	idx: string | null | undefined,
): string {
	const persisted = String(value?.criteriaBlockId || '').trim();
	if (persisted) return persisted;
	return `criteria_${String(idx || '').replace(/[^a-zA-Z0-9_-]/g, '_')}`;
}

// Parse a listing price that may be a number, or a string like "$499,000".
const parseListingPrice = (value: unknown): number => {
	if (typeof value === 'number') return value;
	if (typeof value === 'string') {
		const parsed = parseFloat(value.replace(/[^0-9.]/g, ''));
		return isNaN(parsed) ? 0 : parsed;
	}
	return 0;
};

// Read a listing's "date listed" from whichever field the data source provides.
const getListingDate = (listing: any): number => {
	const raw =
		listing?.dateListed ||
		listing?.listingDisplayDate ||
		listing?.LISTINGDISPLAYDATE ||
		listing?.AddDateTime ||
		listing?.adddatetime ||
		listing?.list_date;
	if (!raw) return 0;
	const time = new Date(raw).getTime();
	return isNaN(time) ? 0 : time;
};

// Sort listings by the block's selected rule. Returns a new array.
const sortListings = <T,>(listings: T[], sortBy?: ListingSortBy): T[] => {
	if (!sortBy) return listings;
	const sorted = [...listings];
	switch (sortBy) {
		case 'price_desc':
			return sorted.sort(
				(a: any, b: any) =>
					parseListingPrice(b.list_price) - parseListingPrice(a.list_price),
			);
		case 'price_asc':
			return sorted.sort(
				(a: any, b: any) =>
					parseListingPrice(a.list_price) - parseListingPrice(b.list_price),
			);
		case 'date_desc':
			return sorted.sort((a: any, b: any) => getListingDate(b) - getListingDate(a));
		case 'date_asc':
			return sorted.sort((a: any, b: any) => getListingDate(a) - getListingDate(b));
		default:
			return sorted;
	}
};

interface ICriteriaBlockAttributes extends Record<string, any> {
	'background-color': string;
	'button-text-color': string;
	'button-color': string;
	'listing-name-color': string;
	'listing-price-color': string;
	'listing-address-color': string;
	'title-font-size': string;
	'button-text': string;
	'show-address': boolean;
	'show-price': boolean;
	'show-details': boolean;
	'show-title': boolean;
	layout: 'single' | 'double' | 'table';
	padding: string;
	title: string;
}

export type ICriteriaBlock = IBlockData<ICriteriaBlockAttributes, ICriteriaBlockData>;

export const ListingsCriteriaBlock = createCustomBlock<ICriteriaBlock>({
	get name() {
		return t('By Criteria');
	},
	type: CustomBlockType.CRITERIA_BLOCK,
	validParentType: [BasicType.PAGE, BasicType.WRAPPER, BasicType.SECTION],
	create: (payload?: RecursivePartial<ICriteriaBlock>) => {
		const defaultData: ICriteriaBlock = {
			type: CustomBlockType.CRITERIA_BLOCK,
			data: {
				value: {
					maxCount: 4,
					sortBy: 'price_desc',
					criteriaConfig: {},
				},
			},
			attributes: {
				'background-color': 'Transparent',
				'button-text-color': '#ffffff',
				'button-color': '#000000',
				'listing-name-color': '#000000',
				'listing-price-color': '#000000',
				'listing-address-color': '#666666',
				'title-font-size': '16px',
				'price-font-size': '18px',
				'address-font-size': '14px',
				'button-font-size': '14px',
				'button-text': 'View Listing',
				'show-title': true,
				'show-address': true,
				'show-price': true,
				'show-details': true,
				layout: 'double',
				padding: '0px 0px 0px 0px',
				title: 'Criteria',
				href: '{{listing.url}}',
				target: '_blank',
			},
			children: [],
		};
		return merge(defaultData, payload);
	},
	render: (params: {
		data: ICriteriaBlock;
		idx?: string | null;
		mode: 'testing' | 'production';
		context?: any;
		dataSource?: { [key: string]: any };
		children?: React.ReactNode;
		keepClassName?: boolean;
		renderPortal?: any;
	}) => {
		const { data } = params;
		const maxCount = data.data.value.maxCount || 4;
		const sortBy = data.data.value.sortBy || 'price_desc';

		// Determine the actual rendering mode
		// 'edit' = editor canvas (show merge tags, maxCount cards, keep {{{{raw}}}})
		// 'preview' = preview/test modal (show fake data, maxCount cards, strip {{{{raw}}}})
		// 'production' = final export (show merge tags, ONE card, keep {{{{raw}}}})
		let actualMode: 'edit' | 'preview' | 'production';

		if (params.mode === 'production') {
			actualMode = 'production';
		} else if (params.dataSource?.previewMode) {
			// Preview/test modal should show fake data
			actualMode = 'preview';
		} else {
			// Editor canvas shows merge tags
			actualMode = 'edit';
		}

		// Get listings from criteria results, ordered by the selected sort rule
		// Re-run the saved query at render time. A criteria block that stored its
		// matches would keep showing sold homes and miss new ones; resolving live
		// means the canvas always reflects what the query returns right now. The
		// legacy stored results are a first paint only, until the search lands.
		const boundCriteria = getCriteriaBlockConfig(data.data.value);
		const liveResults = getCriteriaListings(boundCriteria);
		const listings = sortListings(
			(liveResults as ListingItem[] | undefined) ||
				data.data.value.criteriaResults ||
				[],
			sortBy,
		);
		const selectedLayout = data.attributes.layout || 'double';

		// Process listings with image proxy for all layouts
		// Double layout: adds width, height, and fit parameters for resize (250x167)
		// Other layouts: uses proxy for validation only (no resize parameters)
		const useProxy = selectedLayout === 'double';
		const fallbackUrl = 'https://placehold.co/250x167/e1e1e1/666666?text=No+Image';

		const processedListingsData = listings.map((listing: ListingItem, index: number) => {
			const imageUrl = listing.image_data?.[0];

			// Handle empty/nophoto URLs - a real listing with no photo gets a styled
			// stock placeholder (cycled by index) instead of the plain gray "No Image"
			// box, matching the backend's own fallback for the sent email.
			let validatedUrl: string;
			if (!imageUrl || imageUrl.trim() === '' || imageUrl.includes('nophoto')) {
				validatedUrl = getProxiedImageUrl(
					getListingPlaceholderImage(`placeholder_${(index % 8) + 1}`),
					useProxy ? '250' : undefined,
					useProxy ? '167' : undefined,
					fallbackUrl,
				);
			} else {
				// Use proxy with appropriate resize parameters based on layout
				validatedUrl = getProxiedImageUrl(
					imageUrl,
					useProxy ? '250' : undefined,
					useProxy ? '167' : undefined,
					fallbackUrl,
				);
			}

			return {
				...listing,
				image_data: validatedUrl ? [validatedUrl] : [],
			};
		});

		const limitedListings = processedListingsData.slice(0, maxCount);

		// Get isDesignAdmin from dataSource for conditional rendering
		const isDesignAdmin = params.dataSource?.isDesignAdmin;

		// Determine what to display based on mode
		let displayListings;

		if (actualMode === 'production') {
			// PRODUCTION: One element only (backend will loop with {{#each}})
			if (limitedListings.length > 0) {
				displayListings = [limitedListings[0]];
			} else {
				displayListings = [
					{
						id: 'sample-1',
						address: '123 Sample Street',
						city: 'Anytown',
						stateProvince: 'CA',
						postalCode: '90210',
						list_price: 499000,
						beds: 3,
						baths: 2,
						sq_foot: 1800,
						image_data: ['https://placehold.co/300x200/e1e1e1/666666?text=Sample'],
					},
				];
			}
		} else {
			// EDIT or PREVIEW: Show maxCount cards
			if (limitedListings.length > 0) {
				displayListings = limitedListings;
			} else {
				// Generate maxCount sample listings. Give each a descending price and
				// date so the preview visibly reflects the chosen sort rule.
				const sampleDateBase = new Date('2026-06-01T00:00:00Z').getTime();
				const sampleListings = Array.from({ length: maxCount }, (_, index) => ({
					id: `sample-${index + 1}`,
					address: `${123 + index * 10} Sample Street`,
					city: 'Anytown',
					stateProvince: 'CA',
					postalCode: '90210',
					list_price: 499000 + index * 50000,
					dateListed: new Date(
						sampleDateBase - index * 7 * 86400000,
					).toISOString(),
					beds: 3 + (index % 2),
					baths: 2 + (index % 3),
					sq_foot: 1800 + index * 100,
					image_data: [
						getProxiedImageUrl(
							getListingPlaceholderImage(`placeholder_${(index % 8) + 1}`),
							useProxy ? '250' : undefined,
							useProxy ? '167' : undefined,
							fallbackUrl,
						),
					],
				}));
				displayListings = sortListings(sampleListings, sortBy);
			}
		}

		// Find the layout configuration
		const layoutConfig = listingLayouts.find(
			(layout: any) => layout.id === selectedLayout,
		);
		if (!layoutConfig) {
			console.error(`Layout "${selectedLayout}" not found`);
			return (
				<BasicBlock
					params={params}
					tag='mj-text'
				>
					<div>Layout configuration not found</div>
				</BasicBlock>
			);
		}

		// Prepare handlebars data based on actual mode
		const processedListings = displayListings.map((listing: any, i: number) => {
			const formattedPrice =
				typeof listing.list_price === 'number'
					? '$' + listing.list_price.toLocaleString()
					: listing.list_price
						? '$' + listing.list_price
						: '$0';

			if ((actualMode === 'edit' && isDesignAdmin) || actualMode === 'production') {
				// DSA EDIT or PRODUCTION: Return field names (will become merge tags via {{{{raw}}}})
				return {
					imagePath:
						actualMode === 'production'
							? 'imagePath'
							: getProxiedImageUrl(
									getListingPlaceholderImage(`placeholder_${(i % 8) + 1}`),
									useProxy ? '250' : undefined,
									useProxy ? '167' : undefined,
									fallbackUrl,
								),
					address: 'address',
					city: 'city',
					stateProvince: 'stateProvince',
					postalCode: 'postalCode',
					listPrice: 'listPrice',
					beds: 'beds',
					baths: 'baths',
					sqFoot: 'sqFoot',
					url: 'url',
				};
			} else {
				// PREVIEW or CBAY Edit: Return actual data for realistic preview
				const listingContext = {
					imagePath:
						listing.image_data &&
						Array.isArray(listing.image_data) &&
						listing.image_data.length > 0
							? listing.image_data[0]
							: 'https://placehold.co/300x200/e1e1e1/666666?text=No+Image',
					// address/stateProvince/postalCode aren't real columns on the raw backend
					// row - the picker translates them from streetaddress/state/zip_code at
					// selection time, but that translation doesn't survive a reload, where
					// this resolves straight off the raw row (see resolveAttachedListingMergeTags.ts's
					// resolveByPath and dataFields.js's listing.address/listing.zip entries for
					// the same pattern). Fall back to the raw columns so a reopened design
					// still shows the full address, not just city.
					address: listing.address || listing.streetaddress || '',
					city: listing.city || '',
					stateProvince: listing.stateProvince || listing.state || '',
					postalCode: listing.postalCode || listing.zip_code || '',
					listPrice: formattedPrice,
					beds: listing.beds || 0,
					baths: listing.baths || 0,
					sqFoot: listing.sq_foot ? listing.sq_foot.toLocaleString() : '0',
					// listingurl is the composed absolute link; the raw url column is a
					// relative path only (see resolveAttachedListingMergeTags.ts's
					// resolveByPath - listingurl wins there too when it has a value).
					url: listing.listingurl || listing.url || 'https://example.com/listing',
				};

				return {
					...listingContext,
					previewHref: previewListingUrl(listing.listingurl || listing.url, params.dataSource?.user?.officeDomain),
					listing: { ...listingContext },
					user: params.dataSource?.user || {},
					contact: params.dataSource?.contact || {},
				};
			}
		});

		// Generate required CSS classes for block selection
		const isTest = params.mode === 'testing';
		let cssClasses = 'email-block';
		if (isTest && params.idx) {
			cssClasses = `email-block node-idx-${params.idx} node-type-${data.type}`;
		}

		// Resolve dynamic brand-colour merge tags to real hex in preview/testing mode;
		// in production the raw tag is returned unchanged for send-time resolution
		// (the production regex path below reads these resolved values).
		const resolveColor = (key: string, fallback: string) =>
			resolvePreviewColor(data.attributes[key], params.mode, params.dataSource) ||
			fallback;

		// Prepare template context
		const templateContext = {
			// Essential CSS classes for block selection
			cssClasses,
			// Production-only: the block's own persisted identity spliced onto the
			// root <table> itself, same treatment Image/index.tsx gives
			// data-dynamic-tag on the <img>. Keyed by criteriaBlockId, not the
			// criteriaConfig value itself - the backend looks the filter up by
			// this id instead of reading it off the HTML.
			listingRefAttr:
				actualMode === 'production'
					? buildListingDataAttr({ criteriaId: getCriteriaBlockId(data.data.value, params.idx) })
					: '',
			// Block attributes mapped to handlebars context
			backgroundColor: resolveColor('background-color', 'Transparent').toLowerCase(),
			buttonTextColor: resolveColor('button-text-color', '#ffffff'),
			buttonColor: resolveColor('button-color', '#000000'),
			listingNameColor: resolveColor('listing-name-color', '#000000'),
			listingPriceColor: resolveColor('listing-price-color', '#000000'),
			listingAddressColor: resolveColor('listing-address-color', '#666666'),
			titleFontSize: data.attributes['title-font-size'] || '16px',
			priceFontSize: data.attributes['price-font-size'] || '18px',
			addressFontSize: data.attributes['address-font-size'] || '14px',
			buttonFontSize: data.attributes['button-font-size'] || '14px',
			buttonText: data.attributes['button-text'] || 'View Listing',
			href: resolveListingHref(
				toLoopListingHref(data.attributes['href'] as string),
				actualMode,
				'{{url}}',
			),
			showTitle: data.attributes['show-title'] ?? true,
			showAddress: data.attributes['show-address'] !== false,
			showPrice: data.attributes['show-price'] !== false,
			showDetails: data.attributes['show-details'] !== false,
			padding: data.attributes.padding || '20px',

			// Template content
			title: data.attributes.title || 'Criteria',

			// Listings data
			listings: processedListings,
			hasListings: processedListings.length > 0,
			listingCount: processedListings.length,
		};

		try {
			let htmlContent: string;
			let templateString = layoutConfig.data;
			// The raster service renders at a mobile-width viewport, so the layout's
			// @media rules would stack the cards; the thumbnail keeps the desktop
			// columns like the canvas (monday 13121531553).
			if (params.dataSource?.rasterMode) {
				templateString = templateString.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, '');
			}

			if (actualMode === 'production') {
				// PRODUCTION MODE: Build HTML manually preserving {{#each listings}} for backend
				const eachMatch = templateString.match(
					/\{\{#each listings\}\}(.*?)\{\{\/each\}\}/s,
				);
				if (!eachMatch) {
					throw new Error('Could not find {{#each listings}} block in template');
				}

				const cardTemplate = eachMatch[1];
				const wrapperBefore = templateString.substring(
					0,
					templateString.indexOf('{{#each listings}}'),
				);
				const wrapperAfter = templateString.substring(
					templateString.indexOf('{{/each}}') + '{{/each}}'.length,
				);

				// Replace style variables in wrapper
				const processedWrapperBefore = wrapperBefore
					.replace(/\{\{cssClasses\}\}/g, templateContext.cssClasses)
					.replace(/\{\{\{listingRefAttr\}\}\}/g, templateContext.listingRefAttr)
					.replace(/\{\{backgroundColor\}\}/g, templateContext.backgroundColor)
					.replace(/\{\{padding\}\}/g, templateContext.padding);

				// Process card template - replace parent variables but keep {{{{raw}}}} intact
				let processedCard = cardTemplate
					.replace(/\{\{\.\.\/href\}\}/g, templateContext.href)
					.replace(/\{\{\.\.\/buttonColor\}\}/g, templateContext.buttonColor)
					.replace(/\{\{\.\.\/buttonTextColor\}\}/g, templateContext.buttonTextColor)
					.replace(/\{\{\.\.\/listingNameColor\}\}/g, templateContext.listingNameColor)
					.replace(/\{\{\.\.\/listingPriceColor\}\}/g, templateContext.listingPriceColor)
					.replace(
						/\{\{\.\.\/listingAddressColor\}\}/g,
						templateContext.listingAddressColor,
					)
					.replace(/\{\{\.\.\/titleFontSize\}\}/g, templateContext.titleFontSize)
					.replace(/\{\{\.\.\/priceFontSize\}\}/g, templateContext.priceFontSize)
					.replace(/\{\{\.\.\/addressFontSize\}\}/g, templateContext.addressFontSize)
					.replace(/\{\{\.\.\/buttonFontSize\}\}/g, templateContext.buttonFontSize)
					.replace(/\{\{\.\.\/buttonText\}\}/g, templateContext.buttonText);

				// Handle conditionals
				processedCard = processedCard.replace(
					/\{\{#if \.\.\/showAddress\}\}(.*?)\{\{\/if\}\}/gs,
					templateContext.showAddress ? '$1' : '',
				);
				processedCard = processedCard.replace(
					/\{\{#if \.\.\/showPrice\}\}(.*?)\{\{\/if\}\}/gs,
					templateContext.showPrice ? '$1' : '',
				);
				processedCard = processedCard.replace(
					/\{\{#if \.\.\/showDetails\}\}(.*?)\{\{\/if\}\}/gs,
					templateContext.showDetails ? '$1' : '',
				);
				// buttonText/buttonTextColor were already substituted into both branches
				// above; templateContext always defaults them to a truthy value, so the
				// true branch is always the right one - this just strips the surrounding
				// {{#if}}/{{else}}/{{/if}} markup, which would otherwise survive as
				// literal text in the sent email (e.g. "{{#if ../buttonText}}View
				// Listing{{else}}More Info{{/if}}" instead of just "View Listing").
				processedCard = processedCard.replace(
					/\{\{#if \.\.\/buttonTextColor\}\}(.*?)\{\{else\}\}.*?\{\{\/if\}\}/gs,
					'$1',
				);
				processedCard = processedCard.replace(
					/\{\{#if \.\.\/buttonText\}\}(.*?)\{\{else\}\}.*?\{\{\/if\}\}/gs,
					'$1',
				);

				// Strip {{{{raw}}}} wrappers, leaving merge tags
				processedCard = processedCard.replace(
					/\{\{\{\{raw\}\}\}\}(.*?)\{\{\{\{\/raw\}\}\}\}/gs,
					'$1',
				);

				// Reassemble with {{#each listings}} preserved
				htmlContent =
					processedWrapperBefore +
					'{{#each listings}}' +
					processedCard +
					'{{/each}}' +
					wrapperAfter;
			} else {
				// EDIT or PREVIEW MODE: Use Handlebars compilation
				// Preview links each card to its own listing when Link Address is the listing
				// URL field; sample cards get '' and are unlinked (monday 13122054004).
				if (
					actualMode === 'preview' &&
					isListingUrlTag(toLoopListingHref(data.attributes['href'] as string) || '{{url}}')
				) {
					templateString = templateString.replace(/\{\{\.\.\/href\}\}/g, '{{previewHref}}');
				}

				if (actualMode === 'edit') {
					// Always expose {{imagePath}} for Handlebars evaluation — the URL is always
					// resolved to an actual value in templateContext, so the raw wrapper is not needed.
					templateString = templateString.replace(
						/\{\{\{\{raw\}\}\}\}(\{\{imagePath\}\})\{\{\{\{\/raw\}\}\}\}/g,
						'$1',
					);
				}

				// KEY FIX: In PREVIEW mode and CBAY Edit mode, strip {{{{raw}}}} blocks so Handlebars processes actual data
				// In DSA EDIT and PRODUCTION modes, keep {{{{raw}}}} blocks to preserve merge tags
				if (actualMode === 'preview' || (actualMode === 'edit' && !isDesignAdmin)) {
					templateString = templateString.replace(/\{\{\{\{raw\}\}\}\}/g, '');
					templateString = templateString.replace(/\{\{\{\{\/raw\}\}\}\}/g, '');
				}

				// Compile and render the handlebars template
				const template = Handlebars.compile(templateString);
				htmlContent = template(templateContext);

				// In testing mode, process HTML to replace merge tag image URLs with placeholders
				// This shows blue placeholder images with the merge tag name instead of broken images
				if (params.mode === 'testing') {
					htmlContent = processHtmlMergeTags(htmlContent, params.mode);
				}
				// Canvas/preview cards must not be links (monday 13065458913).
				htmlContent = stripEmptyListingHrefs(htmlContent);
			}

			// The {{{{raw}}}} blocks in EDIT/PRODUCTION modes preserve merge tags for downstream processing

			// NOTE: Only raw, do not use other tags here
			const mjmlContent = `
				<mj-raw>
					${htmlContent}
				</mj-raw>
      `;

			return (
				<BasicBlock
					params={params}
					tag='mj-section'
				>
					{mjmlContent}
				</BasicBlock>
			);
		} catch (error) {
			console.error('Error rendering criteria block template:', error);
			return (
				<BasicBlock
					params={params}
					tag='mj-text'
				>
					<div>Error rendering criteria listings</div>
				</BasicBlock>
			);
		}
	},
});
